import { events } from "@constants/tracking";
import { useSession } from "@features/session/hooks/useSession";
import useSessionStore from "@features/session/hooks/useSessionStore";
import type { BaseItemSchema } from "@memorang/types";
import { router } from "expo-router";
import { useCallback, useState } from "react";
import type { ReportEventParams, TrackReportEvent } from "./useReportAnalytics";

type Options = {
	sessionId?: string | number;
	isSummativeTest: boolean;
	trackReportEvent: TrackReportEvent;
};

/** `trackingContext` names the entry point that sent the user into review. */
export type GoToReviewSession = (
	sessionItems: BaseItemSchema[],
	trackingContext?: ReportEventParams,
) => Promise<void>;

export const useGoToReviewSession = ({
	sessionId,
	isSummativeTest,
	trackReportEvent,
}: Options) => {
	const [isReviewLoading, setIsReviewLoading] = useState(false);
	const updateSession = useSessionStore((state) => state.updateSession);
	const updateCurrentBlockIndex = useSessionStore(
		(state) => state.updateCurrentBlockIndex,
	);
	const { resumeSession } = useSession();

	const goToReviewSession = useCallback<GoToReviewSession>(
		async (sessionItems, trackingContext) => {
			if (sessionId == null || sessionId === "") {
				return;
			}
			if (sessionItems.length === 0) {
				return;
			}

			trackReportEvent(events.reviewAnswersClicked, {
				type: "QUESTION",
				sessionId: Number(sessionId),
				isSummativeTest: isSummativeTest ? 1 : 0,
				// The count being reviewed, not the session total the base context carries.
				numItems: sessionItems.length,
				...trackingContext,
			});
			setIsReviewLoading(true);
			try {
				const response = await resumeSession({
					sessionId: Number(sessionId),
				});

				updateCurrentBlockIndex(0);
				updateSession({
					sessionResponse: {
						...response,
						sessionItems: [sessionItems],
					},
					isSummativeTest,
					inReviewMode: true,
				});

				router.push({
					pathname: "/(protected)/session/[id]/review",
					params: {
						id: String(sessionId),
						index: "1",
					},
				});
			} finally {
				setIsReviewLoading(false);
			}
		},
		[
			sessionId,
			resumeSession,
			updateCurrentBlockIndex,
			updateSession,
			isSummativeTest,
			trackReportEvent,
		],
	);

	return { goToReviewSession, isReviewLoading };
};
